import { lazy } from "react";

const Stocks = lazy(() =>
  import("../Inventory/Stock/StockList/Stocks")
);
const GoodsReceipts = lazy(() =>
  import("../Inventory/Stock/GoodsReceiptList/GoodsReceipts")
);
const ViewGoodsReceipt = lazy(() =>
  import("../Inventory/Stock/ViewGoodsReceipt/ViewGoodsReceipt")
);
const OrderAndReceiveForm = lazy(() =>
  import("../Inventory/Stock/OrderAndReceiveForm/OrderAndReceiveForm")
);
const ViewOrder = lazy(() =>
  import("../Inventory/Stock/ViewOrder/ViewOrder")
);
const ReceivedDeliveryForm = lazy(() =>
  import("../Inventory/Stock/ReceivedDeliveryForm/ReceivedDeliveryForm")
);
const StockRequestList = lazy(() =>
  import("../Inventory/StockTransfer/Request/List/StockRequestList")
);
const StockTransferList = lazy(() =>
  import("../Inventory/StockTransfer/Transfer/List/StockTransferList")
);
const StockRequestForm = lazy(() =>
  import("../Inventory/StockTransfer/Request/Form/StockRequestForm")
);
const ViewStockRequest = lazy(() =>
  import("../Inventory/StockTransfer/Request/View/ViewStockRequest")
);
const ViewStockTransfer = lazy(() =>
  import("../Inventory/StockTransfer/Transfer/View/ViewStockTransfer")
);
const DispatchForm = lazy(() =>
  import("../Inventory/StockTransfer/Transfer/Dispatch/DispatchForm")
);
const ReceiveForm = lazy(() =>
  import("../Inventory/StockTransfer/Transfer/Receive/ReceiveForm")
);

const SpecialOrders = lazy(() =>
  import("../Inventory/SpecialOrder/SpecialOrders")
);
const SpecialOrderForm = lazy(() =>
  import("../Inventory/SpecialOrder/SpecialOrderForm")
);
const StockReturns = lazy(() =>
  import("../Inventory/StockReturn/List/StockReturns")
);
const StockReturnForm = lazy(() =>
  import("../Inventory/StockReturn/Form/PurchaseReturnForm")
);
const ViewReturnStock = lazy(() =>
  import("../Inventory/StockReturn/ViewReturn/ViewReturnStock")
);
const PrintLabelHistory = lazy(() =>
  import("../Inventory/PrintProductLabel/PrintLabelHistory/PrintLabelHistory")
);
const PrintLabelForm = lazy(() =>
  import("../Inventory/PrintProductLabel/PrintNewLabel/PrintLabelForm")
);
const InvCounts = lazy(() =>
  import("../Inventory/InventoryCount/Counts/InvCounts")
);
const AddEditInvCount = lazy(() =>
  import("../Inventory/InventoryCount/AddEditInvCount/AddEditInvCount")
);
const InvCounting = lazy(() =>
  import("../Inventory/InventoryCount/Counting/InvCounting")
);
const ReviewCounting = lazy(() =>
  import("../Inventory/InventoryCount/Counting/ReviewCounting/ReviewCounting")
);
const InventoryAdjustmentHistory = lazy(() =>
  import("../Inventory/InventoryAdjustment/List/InventoryAdjustmentHistory")
);
const InventoryAdjustmentForm = lazy(() =>
  import("../Inventory/InventoryAdjustment/Form/InventoryAdjustmentForm")
);
const SupplierBillList = lazy(() =>
  import("../Inventory/PurchaseInvoice/SupplierBillList/SupplierBillList")
);
const SupplierBillForm = lazy(() =>
  import("../Inventory/PurchaseInvoice/SupplierBillForm/SupplierBillForm")
);
const ViewSupplierBill = lazy(() =>
  import("../Inventory/PurchaseInvoice/ViewSupplierBill/ViewSupplierBill")
);

export const inventoryRoutes = (access, settings) => {
  if (!settings?.fiscalYearId) return [];

  const routes = [];

  // Moved off catalogAccess — the flag that opens the product catalogue — onto the
  // module's own grant. The verbs inside it (send / receive / settle) are separate
  // rights again, because they are three different people's jobs.
  if (access.stockTransferAccess) {
    routes.push(
      // Two screens, deliberately. They answer two different questions — "what has
      // someone asked me for" and "what is on the road right now" — and each carries its
      // own count in the menu so an empty one is never mistaken for a lost document.
      { path: "/stock_requests", element: StockRequestList, title: "stockRequests" },
      { path: "/stock_request/:id", element: ViewStockRequest, title: "stockRequest" },
      // /stock_transfer_history keeps its URL: it is bookmarked, it is in the global
      // search index, and renaming it would break both for no user-visible gain.
      { path: "/stock_transfer_history", element: StockTransferList, title: "stockTransfers" },
      { path: "/stock_transfer/:id", element: ViewStockTransfer, title: "stockTransfer" },
    );

    if (access.stockTransferNav?.create)
      routes.push(
        { path: "/stock_request_form", element: StockRequestForm, title: "stockRequestForm" },
        { path: "/stock_request_form/:id", element: StockRequestForm, title: "stockRequestForm" },
      );

    // Answering a request is what `dispatch` means on its own.
    if (access.stockTransferNav?.dispatch)
      routes.push(
        { path: "/stock_transfer_dispatch/:requestId", element: DispatchForm, title: "sendStock" },
      );

    // Sending with no request behind it is a second grant on top. Registered
    // separately so the bare URL simply does not exist for someone without it —
    // the server rejects the post either way, this only saves them the round trip.
    if (access.stockTransferNav?.dispatch && access.stockTransferNav?.directSend)
      routes.push(
        { path: "/stock_transfer_dispatch", element: DispatchForm, title: "sendStock" },
      );

    if (access.stockTransferNav?.receive)
      routes.push({ path: "/stock_transfer_receive/:id", element: ReceiveForm, title: "receiveStock" });
  }


  if (access?.inventoryCountAccess) {
    routes.push(
      { path: "/inventory_counts", element: InvCounts, title: "inventoryCounts" },
      { path: "/inv_counting/:id", element: InvCounting, title: "inventoryCounting" },
      { path: "/review_counting/:id", element: ReviewCounting, title: "reviewCounting" },
      { path: "/add_edit_count", element: AddEditInvCount, title: "addInventoryCount" },
      { path: "/add_edit_count/:id", element: AddEditInvCount, title: "editInventoryCount" },
    );
  }

  if (access.printLabelAccess) {
    routes.push(
      { path: "/print_label_history", element: PrintLabelHistory, title: "printLabelHistory" },
      { path: "/print_new_label", element: PrintLabelForm, title: "printNewLabel" },
    );
  }

  // Purchase Order — gated independently of Goods Receipt.
  if (access?.stockAccess) {
    routes.push(
      { path: "/purchase_orders", element: Stocks, title: "stockControl" },
      { path: "/view_purchase_order/:id", element: ViewOrder, title: "viewOrder" },
    );

    if (access.stock?.edit) {
      routes.push({ path: "/purchase_order_form/:id", element: OrderAndReceiveForm, title: "editStockOrder" });
    }
    if (access.stock?.create) {
      routes.push({ path: "/purchase_order_form", element: OrderAndReceiveForm, title: "orderStock" });
    }
  }

  // Goods Receipt (GRN) — its own access control. The receive forms create a GRN
  // (PO-linked or standalone), so they are gated by goodsReceipt.create.
  if (access?.goodsReceiptAccess) {
    routes.push(
      { path: "/goods_receipts", element: GoodsReceipts, title: "goodsReceipts" },
      { path: "/view_goods_receipt/:id", element: ViewGoodsReceipt, title: "viewGoodsReceipt" },
    );

    if (access.goodsReceiptNav?.create) {
      routes.push(
        { path: "/receive_stock_form", element: OrderAndReceiveForm, title: "receiveStock", props: { isReceiveForm: true } },
        { path: "/receive_delivery_form/:id", element: ReceivedDeliveryForm, title: "receiveDelivery" },
      );
    }
  }

  if (access?.stockAccess) {
    routes.push(
      { path: "/special_orders", element: SpecialOrders, title: "specialOrders" },
      { path: "/special_order_form", element: SpecialOrderForm, title: "specialOrderForm" },
      { path: "/special_order_form/:id", element: SpecialOrderForm, title: "specialOrderForm" },
    );
  }

  if (access?.inventoryAdjustmentAccess) {
    routes.push(
      { path: "/inventory_adjustments", element: InventoryAdjustmentHistory, title: "inventoryAdjustments" },
      { path: "/inventory_adjustment_form", element: InventoryAdjustmentForm, title: "inventoryAdjustmentForm" },
      { path: "/inventory_adjustment_form/:id", element: InventoryAdjustmentForm, title: "inventoryAdjustmentForm" },
    );
  }

  if (access?.supplierBillAccess) {
    routes.push(
      { path: "/purchase_invoices", element: SupplierBillList, title: "purchaseInvoices" },
      { path: "/view_purchase_invoice/:id", element: ViewSupplierBill, title: "viewPurchaseInvoice" },
    );
    if (access.supplierBill?.create) {
      routes.push(
        { path: "/purchase_invoice_form", element: SupplierBillForm, title: "purchaseInvoiceForm" },
        { path: "/purchase_invoice_form/:id", element: SupplierBillForm, title: "purchaseInvoiceForm" },
      );
    }
  }

  if (access?.stockReturnAccess) {
    routes.push(
      { path: "/purchase_returns", element: StockReturns, title: "stockReturns" },
      { path: "/view_purchase_return/:id", element: ViewReturnStock, title: "viewStockReturn" },
    );
    if (access?.stockReturn?.create) {
      // No :id variant — a purchase return posts immediately and is corrected by voiding,
      // so there is nothing to re-open for editing. The old route existed but the form
      // never read useParams(), so it silently rendered a blank new return.
      routes.push(
        { path: "/purchase_return_form", element: StockReturnForm, title: "stockReturnForm" },
      );
    }
  }

  return routes;
};
